import { useEffect, useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { getApiUrl } from "@/lib/config";
import { useLanguage } from "@/components/language-provider";
import { Bell, Eye, Pen, XCircle, CheckCheck, Clock, X } from "lucide-react";
import { Link } from "wouter";
import { formatDistanceToNow } from "date-fns";
import { ptBR, enUS } from "date-fns/locale";

interface Notification {
  id: string;
  type: string;
  signerName: string;
  signerEmail: string;
  documentName: string;
  documentId: string;
  message: string;
  createdAt: string;
}

const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem("authToken");
  if (token) {
    return { Authorization: `Bearer ${token}` };
  }
  return {};
};

export function NotificationPopup() {
  const { language } = useLanguage();
  const [visibleNotifications, setVisibleNotifications] = useState<Notification[]>([]);
  const [lastCheckedCount, setLastCheckedCount] = useState(0);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Check if user is logged in
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    setIsLoggedIn(!!token);
  }, []);

  // Poll for new notifications every 30 seconds
  const { data } = useQuery({
    queryKey: ["/api/notifications/recent"],
    queryFn: async () => {
      const res = await fetch(getApiUrl("/api/notifications/recent?count=5"), {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error("Failed to fetch notifications");
      return res.json();
    },
    enabled: isLoggedIn,
    refetchInterval: 30000, // 30 seconds
    staleTime: 15000,
  });

  const notifications: Notification[] = data?.data?.notifications || [];
  const popupEnabled = data?.data?.popupEnabled ?? true;

  // Show new notifications as popup
  useEffect(() => {
    if (!popupEnabled || !notifications.length) return;

    const currentCount = notifications.length;
    
    // If there are new notifications, show them
    if (currentCount > lastCheckedCount || lastCheckedCount === 0) {
      const newNotifications = notifications.slice(0, currentCount - lastCheckedCount || 1);
      
      newNotifications.forEach((notification, index) => {
        setTimeout(() => {
          setVisibleNotifications(prev => {
            // Don't add duplicates
            if (prev.find(n => n.id === notification.id)) return prev;
            return [...prev, notification].slice(-3); // Keep max 3 visible
          });

          // Auto-hide after 8 seconds
          setTimeout(() => {
            setVisibleNotifications(prev => prev.filter(n => n.id !== notification.id));
          }, 8000);
        }, index * 500); // Stagger notifications
      });
    }

    setLastCheckedCount(currentCount);
  }, [notifications, popupEnabled, lastCheckedCount]);

  const dismissNotification = useCallback((id: string) => {
    setVisibleNotifications(prev => prev.filter(n => n.id !== id));
    
    // Mark as read
    fetch(getApiUrl(`/api/notifications/${id}/read`), {
      method: "PATCH",
      headers: getAuthHeaders(),
    }).then(() => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
    });
  }, []);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "DocumentViewed":
        return <Eye className="h-5 w-5 text-blue-500" />;
      case "DocumentSigned":
        return <Pen className="h-5 w-5 text-green-500" />;
      case "DocumentDeclined":
        return <XCircle className="h-5 w-5 text-red-500" />;
      case "DocumentCompleted":
        return <CheckCheck className="h-5 w-5 text-emerald-500" />;
      case "SignatureExpired":
        return <Clock className="h-5 w-5 text-orange-500" />;
      default:
        return <Bell className="h-5 w-5 text-gray-500" />;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return formatDistanceToNow(date, {
      addSuffix: true,
      locale: language === "pt" ? ptBR : enUS,
    });
  };

  if (!isLoggedIn || !popupEnabled || visibleNotifications.length === 0) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 z-50 flex flex-col gap-2 max-w-sm">
      {visibleNotifications.map((notification, index) => (
        <div
          key={notification.id}
          className="animate-in slide-in-from-left-5 fade-in-0 duration-300"
          style={{ animationDelay: `${index * 100}ms` }}
        >
          <div className="bg-background border rounded-lg shadow-lg p-4 relative overflow-hidden">
            {/* Progress bar for auto-dismiss */}
            <div className="absolute top-0 left-0 h-1 bg-primary/20 w-full">
              <div 
                className="h-full bg-primary animate-shrink"
                style={{ animationDuration: "8s" }}
              />
            </div>
            
            <button
              onClick={() => dismissNotification(notification.id)}
              className="absolute top-2 right-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
            
            <div className="flex items-start gap-3 pr-6">
              <div className="flex-shrink-0 mt-0.5">
                {getNotificationIcon(notification.type)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium line-clamp-2">
                  {notification.message}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {formatDate(notification.createdAt)}
                </p>
              </div>
            </div>
            
            <div className="mt-3 flex items-center justify-between">
              <Link 
                href="/notifications"
                onClick={() => dismissNotification(notification.id)}
              >
                <span className="text-xs text-primary hover:underline cursor-pointer">
                  Ver todas notificações
                </span>
              </Link>
              {notification.documentId && (
                <Link 
                  href={`/documents/${notification.documentId}`}
                  onClick={() => dismissNotification(notification.id)}
                >
                  <span className="text-xs text-primary hover:underline cursor-pointer">
                    Ver documento
                  </span>
                </Link>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// CSS animation for progress bar (add to index.css)
// @keyframes shrink {
//   from { width: 100%; }
//   to { width: 0%; }
// }
// .animate-shrink {
//   animation: shrink linear forwards;
// }
